import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/ui/dialog';
import { Badge } from '../../../components/ui/badge';
import { FileText, Image as ImageIcon, Zap, Play, Film, Mic, File as FileIcon } from 'lucide-react';
import api, { apiError, mediaUrl } from '../../../lib/api';
import { TYPE_ICONS } from './utils';
import { PickerVoice } from './VoiceNote';

function HoverZoom({ media, children }) {
  const [show, setShow] = useState(false);
  const zoomable = media?.url && (media.type === 'photo' || media.type === 'video');
  return (
    <div onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)} className="relative">
      {children}
      {show && zoomable && (
        <div className="fixed z-[200] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none rounded-2xl overflow-hidden border-2 border-white/20 shadow-2xl bg-black" data-testid="hover-zoom-preview">
          {media.type === 'photo'
            ? <img src={mediaUrl(media.url)} alt="" className="max-h-[60vh] max-w-[45vw] object-contain" />
            : <video src={mediaUrl(media.url)} autoPlay muted loop className="max-h-[60vh] max-w-[45vw]" />}
        </div>
      )}
    </div>
  );
}

function PickerThumb({ v, sent }) {
  const Ic = TYPE_ICONS[v.type] || ImageIcon;
  return (
    <HoverZoom media={v}>
      <div className={`w-16 h-16 rounded-lg overflow-hidden border border-white/10 bg-[#101013] relative ${sent ? 'opacity-50 grayscale' : ''}`}>
        {v.url && v.type === 'photo' ? (
          <img src={mediaUrl(v.url)} alt={v.name} className="w-full h-full object-cover" />
        ) : v.url && v.type === 'video' ? (
          <video src={mediaUrl(v.url)} muted loop playsInline preload="metadata" className="w-full h-full object-cover"
            onMouseEnter={e => e.target.play().catch(() => {})} onMouseLeave={e => { e.target.pause(); e.target.currentTime = 0; }} />
        ) : v.url && v.type === 'voice' ? (
          <PickerVoice url={mediaUrl(v.url)} />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><Ic size={16} className="text-white/30" /></div>
        )}
        {sent && (
          <span className="absolute inset-0 flex items-center justify-center pointer-events-none bg-black/60">
            <span className="text-[8px] font-bold text-amber-300 tracking-wider bg-black/80 px-1.5 py-0.5 rounded border border-amber-500/40">ENVOYÉ</span>
          </span>
        )}
      </div>
    </HoverZoom>
  );
}

export function ScriptsPickerDialog({ open, onClose, scripts, tags, vault, sentIds, paidIds, botIds = [], sentVaultIds = [], onInsert }) {
  const [tag, setTag] = useState('all');
  const filtered = scripts.filter(s => tag === 'all' || s.tag === tag);
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="bg-[#0B0B0E] border-white/10 text-slate-200 max-w-4xl">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-xl bg-sky-500/15 border border-sky-500/30 flex items-center justify-center"><FileText size={16} className="text-sky-400" /></span>
            Scripts
          </DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">Choisis un tag, puis insère un script (avec ou sans lien)</DialogDescription>
        </DialogHeader>
        <div className="flex max-h-[62vh] min-h-[320px] pt-1">
          <div className="w-44 shrink-0 border-r border-white/5 pr-3 overflow-y-auto scrollbar-thin">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-1">Tags</div>
            <div className="text-[10px] text-slate-600 px-1 mb-2">{tags.length} tag(s)</div>
            <div className="space-y-1">
              <button onClick={() => setTag('all')} className={`w-full text-left px-3 py-2 rounded-xl text-xs flex items-center gap-2 border ${tag === 'all' ? 'border-sky-500/40 bg-sky-500/10 text-white' : 'border-transparent text-slate-400 hover:bg-white/5'}`}>
                Tous les scripts <span className="ml-auto text-[9px] px-1.5 py-0.5 rounded bg-white/10 text-slate-400">{scripts.length}</span>
              </button>
              {tags.map(t => (
                <button key={t.id} data-testid={`picker-tag-${t.name}`} onClick={() => setTag(t.name)} className={`w-full text-left px-3 py-2 rounded-xl text-xs flex items-center gap-2 border ${tag === t.name ? 'border-sky-500/40 bg-sky-500/10 text-white' : 'border-transparent text-slate-400 hover:bg-white/5'}`}>
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: t.color }} /> {t.name}
                  <span className="ml-auto text-[9px] px-1.5 py-0.5 rounded bg-white/10 text-slate-400">{scripts.filter(s => s.tag === t.name).length}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto scrollbar-thin pl-4">
            <table className="w-full">
              <thead className="sticky top-0 bg-[#0B0B0E] z-10">
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-white/5">
                  <th className="py-2 pr-3 font-semibold w-36">Script</th>
                  <th className="py-2 font-semibold">Contenu et médias · {filtered.length}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filtered.map(s => {
                  const sent = sentIds.includes(s.id);
                  const paid = !!(paidIds && paidIds.has && paidIds.has(s.id));
                  const parBot = (botIds || []).includes(s.id);  // 15/09 : « Bienvenue » déjà fait par le bot d'accueil
                  const medias = (s.vault_ids || []).map(id => vault.find(v => v.id === id)).filter(Boolean);
                  return (
                    <tr key={s.id} data-testid={`picker-script-${s.id}`} onClick={() => onInsert(s)} className={`cursor-pointer hover:bg-white/[0.03] transition-colors ${(sent && !paid) || parBot ? 'opacity-50' : ''}`}>
                      <td className="py-3.5 pr-3 align-top">
                        <div className="text-sm font-semibold text-white">{s.title}</div>
                        {paid
                          ? <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[8px] px-1.5 mt-1.5 tracking-wider" data-testid={`picker-paid-${s.id}`}>✓ PAYÉ</Badge>
                          : sent && s.price > 0
                            // PAID script already sent but no payment recorded yet → red "à relancer".
                            ? <Badge className="bg-rose-500/15 text-rose-300 border-rose-500/30 text-[8px] px-1.5 mt-1.5 tracking-wider" data-testid={`picker-sent-${s.id}`}>✗ PAS PAYÉ</Badge>
                            // FREE script already sent → neutral "déjà envoyé". Nothing to pay,
                            // so it must NEVER show the red "pas payé" badge.
                            : sent && <Badge className="bg-white/5 text-slate-400 border-white/10 text-[8px] px-1.5 mt-1.5 tracking-wider" data-testid={`picker-free-sent-${s.id}`}>✓ ENVOYÉ</Badge>}
                        {parBot && !sent && <Badge className="bg-white/5 text-slate-400 border-white/10 text-[8px] px-1.5 mt-1.5" data-testid={`picker-bot-${s.id}`}>🤖 déjà fait par le bot</Badge>}
                        {/* Statut MÉDIAS : si tous/partie des médias du PPV ont déjà été envoyés à ce fan (même si le script entier ne l'a pas été). Partagé entre chatteurs. */}
                        {(() => {
                          const tot = (s.vault_ids || []).length;
                          if (!tot || sent) return null;
                          const ms = (s.vault_ids || []).filter(id => (sentVaultIds || []).includes(id)).length;
                          if (ms === 0) return null;
                          return ms === tot
                            ? <Badge className="bg-white/5 text-slate-400 border-white/10 text-[8px] px-1.5 mt-1.5 tracking-wider" data-testid={`picker-media-done-${s.id}`} title="Tous les médias de ce PPV ont déjà été envoyés à ce fan">✓ MÉDIAS ENVOYÉS</Badge>
                            : <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 text-[8px] px-1.5 mt-1.5 tracking-wider" data-testid={`picker-media-partial-${s.id}`} title="Le fan a déjà vu une partie des médias de ce PPV">PARTIEL {ms}/{tot}</Badge>;
                        })()}
                      </td>
                      <td className="py-3.5 align-top">
                        <div className="flex items-start justify-between gap-3">
                          <div className="text-xs text-slate-300 line-clamp-2 whitespace-pre-wrap flex-1">{s.body}{s.link ? `\n${s.link}` : ''}</div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            {medias.length > 0 && <Badge variant="outline" className="border-white/10 bg-white/5 text-slate-300 text-[9px] px-1.5"><ImageIcon size={8} className="mr-0.5" /> {medias.length}</Badge>}
                            {s.link && <Badge variant="outline" className="border-sky-500/30 bg-sky-500/10 text-sky-300 text-[9px] px-1.5"><Zap size={8} className="mr-0.5" /> Lien</Badge>}
                            <Badge variant="outline" className="border-white/10 bg-white/5 text-slate-300 text-[9px] px-1.5">{s.price > 0 ? `${s.price}$` : 'Gratuit'}</Badge>
                          </div>
                        </div>
                        {medias.length > 0 && (
                          <div className="flex gap-2 mt-2.5">
                            {medias.map(v => <PickerThumb key={v.id} v={v} sent={sent} />)}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && <tr><td colSpan={2} className="text-center text-xs text-slate-500 py-10">Aucun script dans ce tag.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function VaultPickerDialog({ open, onClose, vault, sentIds, onPick, onCreerLien }) {
  const [type, setType] = useState('all');
  const [tag, setTag] = useState('');
  const [sel, setSel] = useState([]);
  useEffect(() => { if (open) { setSel([]); setTag(''); } }, [open]);
  const allTags = Array.from(new Set(vault.flatMap(v => v.tags || [])));
  const filtered = vault.filter(v =>
    (type === 'all' || v.type === type) &&
    (!tag || (v.tags || []).includes(tag))
  );
  const toggle = (v) => setSel(s => (s.some(x => x.id === v.id) ? s.filter(x => x.id !== v.id) : [...s, v]));
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="bg-[#0B0B0E] border-white/10 text-slate-200 max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-white">Médias du Vault — envoyés gratuitement</DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">Clique pour sélectionner un ou plusieurs médias, puis attache-les.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-1.5 pt-1">
          {['all', 'photo', 'video', 'voice', 'document'].map(t => (
            <button key={t} onClick={() => setType(t)} className={`px-2.5 py-1 rounded-lg text-[11px] capitalize ${type === t ? 'bg-indigo-500 text-white' : 'bg-white/5 text-slate-400'}`}>{t === 'all' ? 'Tous les types' : t}</button>
          ))}
        </div>
        {allTags.length > 0 && (
          <div className="flex gap-1.5 pt-1 flex-wrap" data-testid="vault-picker-tags">
            <button onClick={() => setTag('')} className={`px-2.5 py-1 rounded-full text-[11px] ${!tag ? 'bg-white text-slate-900 font-semibold' : 'bg-white/5 text-slate-400'}`}>Tous les tags</button>
            {allTags.map(t => (
              <button key={t} data-testid={`vault-picker-tag-${t}`} onClick={() => setTag(tag === t ? '' : t)}
                className={`px-2.5 py-1 rounded-full text-[11px] border ${tag === t ? 'bg-fuchsia-500/25 border-fuchsia-400/50 text-fuchsia-200' : 'border-white/10 bg-white/5 text-slate-400 hover:text-white'}`}>
                🏷️ {t}
              </button>
            ))}
          </div>
        )}
        <div className="grid grid-cols-3 sm:grid-cols-4 auto-rows-[30vw] sm:auto-rows-[150px] gap-2.5 max-h-[54vh] overflow-y-auto scrollbar-thin pt-2 pr-1">
          {filtered.length === 0 && (
            <div className="col-span-full py-12 text-center text-sm text-slate-500">Aucun média dans ce vault pour ce filtre.</div>
          )}
          {filtered.map(v => {
            const sent = sentIds.includes(v.id);
            const selIdx = sel.findIndex(x => x.id === v.id);
            const isSel = selIdx >= 0;
            return (
              <button
                key={v.id}
                data-testid={`picker-vault-${v.id}`}
                onClick={() => toggle(v)}
                title={v.name}
                className={`group relative w-full block rounded-xl overflow-hidden border-2 transition-all ${isSel ? 'border-indigo-400 ring-2 ring-indigo-400/40' : sent ? 'border-amber-500/40 hover:border-indigo-400/60' : 'border-white/10 hover:border-indigo-400/60'} bg-gradient-to-br from-slate-800/60 to-slate-900/60 ${sent && !isSel ? 'grayscale' : ''}`}
              >
                {/* media / poster — cell height comes from the grid's explicit
                    auto-rows (aspect-square / %-padding don't size grid row tracks
                    when every layer below is absolutely positioned). */}
                {v.type === 'photo' ? (
                  <img src={mediaUrl(v.url || `/api/vault/${v.id}/thumb`)} alt={v.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                ) : v.type === 'video' ? (
                  <>
                    <img src={mediaUrl(`/api/vault/${v.id}/thumb`)} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="w-11 h-11 rounded-full bg-black/45 backdrop-blur-sm flex items-center justify-center border border-white/30 group-hover:bg-indigo-500/70 transition-colors">
                        <Play size={18} className="text-white ml-0.5" fill="currentColor" />
                      </span>
                    </div>
                  </>
                ) : v.type === 'voice' ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-gradient-to-br from-indigo-500/20 to-fuchsia-500/15">
                    <span className="w-11 h-11 rounded-full bg-indigo-500/25 border border-indigo-400/40 flex items-center justify-center"><Mic size={18} className="text-indigo-200" /></span>
                    <div className="flex items-end gap-[2px] h-4">
                      {[6, 11, 8, 14, 7, 12, 5, 10].map((h, i) => <span key={i} className="w-[2px] rounded-full bg-indigo-300/70" style={{ height: h }} />)}
                    </div>
                  </div>
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-gradient-to-br from-slate-700/30 to-slate-800/30">
                    <FileIcon size={26} className="text-slate-300" />
                  </div>
                )}

                {/* type pill */}
                <span className="absolute top-1.5 left-1.5 flex items-center gap-1 rounded-md bg-black/55 backdrop-blur-sm px-1.5 py-0.5 text-[9px] font-medium text-white capitalize border border-white/10">
                  {v.type === 'photo' ? <ImageIcon size={9} /> : v.type === 'video' ? <Film size={9} /> : v.type === 'voice' ? <Mic size={9} /> : <FileText size={9} />}
                  {v.type}
                </span>

                {/* name scrim */}
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent px-2 pt-4 pb-1.5">
                  <span className="block text-[10px] text-white truncate font-medium">{v.name}</span>
                </div>

                {/* selection number */}
                {isSel && (
                  <span className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-indigo-500 text-white text-[11px] font-bold flex items-center justify-center border-2 border-white shadow-lg" data-testid={`picker-vault-selected-${v.id}`}>{selIdx + 1}</span>
                )}

                {/* already-sent badge */}
                {sent && !isSel && (
                  <span className="absolute top-1.5 right-1.5 rounded-md bg-amber-500 text-amber-950 border-0 font-bold text-[8px] px-1.5 py-0.5 tracking-wider">ENVOYÉ</span>
                )}
              </button>
            );
          })}
        </div>
        <div className="flex items-center justify-between pt-2 border-t border-white/5">
          <span className="text-xs text-slate-500" data-testid="vault-picker-count">{sel.length === 0 ? 'Aucun média sélectionné' : `${sel.length} média(s) sélectionné(s)`}</span>
          <div className="flex gap-2">
            <button onClick={() => setSel([])} disabled={sel.length === 0} className="px-3 py-2 rounded-lg text-xs text-slate-400 hover:text-white disabled:opacity-30">Effacer</button>
            <button data-testid="vault-picker-attach" onClick={() => onPick(sel)} disabled={sel.length === 0}
              className="px-4 py-2 rounded-lg text-xs font-semibold bg-indigo-500 hover:bg-indigo-600 text-white disabled:opacity-40 disabled:cursor-not-allowed">
              Attacher {sel.length > 0 ? sel.length : ''} média(s)
            </button>
            {onCreerLien && (
              <button data-testid="vault-picker-lien" onClick={() => onCreerLien(sel)}
                disabled={sel.length === 0 || sel.some(x => x.type !== 'photo' && x.type !== 'video')}
                title={sel.some(x => x.type !== 'photo' && x.type !== 'video') ? 'Seules les photos et les vidéos se vendent par lien' : 'Créer un lien Dropp payant avec ces médias'}
                className="px-4 py-2 rounded-lg text-xs font-semibold bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-40 disabled:cursor-not-allowed">
                💶 Créer un lien payant
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Lien payant personnalisé (13/09) : les médias cochés dans le Vault deviennent un lien Dropp unique pour CE fan.
export function LienPayantDialog({ open, onClose, conv, medias, onCree }) {
  const [nom, setNom] = useState('');
  const [prix, setPrix] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState('');
  useEffect(() => { if (open) { setNom(''); setPrix(''); setErreur(''); setEnCours(false); } }, [open]);
  const prixNum = Number(String(prix).replace(',', '.'));
  const valide = nom.trim().length > 0 && prixNum >= 1 && prixNum <= 2000;
  const aVideo = (medias || []).some(m => m.type === 'video');
  const creer = async () => {
    if (!valide || enCours) return;
    setEnCours(true);
    setErreur('');
    try {
      const { data } = await api.post(`/conversations/${conv.id}/lien-payant`,
        { vault_ids: medias.map(m => m.id), prix_eur: prixNum, nom: nom.trim() }, { timeout: 240000 });
      onCree(data);
    } catch (e) {
      setErreur(apiError(e, 'Lien non créé'));
      setEnCours(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={v => !v && !enCours && onClose()}>
      <DialogContent className="bg-[#0B0B0E] border-white/10 text-slate-200 max-w-md" data-testid="lien-payant-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">💶 Créer un lien payant</DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">
            {medias?.length || 0} média(s) pour {conv?.name || 'ce fan'}. Le lien est unique : la vente sera attribuée automatiquement.
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-1.5 overflow-x-auto pb-1">
          {(medias || []).map(m => (
            <img key={m.id} src={mediaUrl(m.type === 'photo' ? (m.url || `/api/vault/${m.id}/thumb`) : `/api/vault/${m.id}/thumb`)} alt={m.name}
              className="w-14 h-14 rounded-lg object-cover border border-white/10 shrink-0" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          ))}
        </div>
        <label className="block text-[11px] text-slate-400 mt-1">Nom du lien (le fan le voit)
          <input data-testid="lien-payant-nom" value={nom} onChange={e => setNom(e.target.value)} maxLength={80} disabled={enCours}
            placeholder="Ex. : Ta vidéo perso en tenue rouge"
            className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm text-white outline-none focus:border-emerald-400/60" />
        </label>
        <label className="block text-[11px] text-slate-400">Prix en euros
          <input data-testid="lien-payant-prix" value={prix} onChange={e => setPrix(e.target.value)} inputMode="decimal" disabled={enCours}
            placeholder="Ex. : 45"
            className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm text-white outline-none focus:border-emerald-400/60" />
        </label>
        {prix !== '' && !(prixNum >= 1 && prixNum <= 2000) && <div className="text-[11px] text-amber-300">Prix entre 1 € et 2 000 €.</div>}
        {erreur && <div className="text-[11px] text-rose-300" data-testid="lien-payant-erreur">{erreur}</div>}
        {enCours && (
          <div className="text-[11px] text-slate-400" data-testid="lien-payant-attente">
            Envoi des médias chez Dropp puis création du lien…{aVideo ? ' (jusqu\'à 1 minute avec une vidéo)' : ''}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
          <button onClick={onClose} disabled={enCours} className="px-3 py-2 rounded-lg text-xs text-slate-400 hover:text-white disabled:opacity-30">Annuler</button>
          <button data-testid="lien-payant-creer" onClick={creer} disabled={!valide || enCours}
            className="px-4 py-2 rounded-lg text-xs font-semibold bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-40 disabled:cursor-not-allowed">
            {enCours ? 'Création…' : 'Créer le lien'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
